import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Spinner from '../../../components/Spinner';
import Button from '../../../components/Button';
import StatusBadge from '../../../components/StatusBadge';
import CustomizedSwitch from '../../../components/Switch';
import RenewalModal from '../../../components/RenewalModal';
import PlanChangeModal from '../../../components/PlanChangeModal';
import Toast from '../../../components/Toast';
import SubscriptionService from '../../../services/subscription.service';
import TenantService from '../../../services/tenant.service';
import AuditService, { AuditLogEntry } from '../../../services/audit.service';
import { usePermission } from '../../../hooks/usePermission';
import { Modules, AuthRoutes, RouteParamsTypes, SortOrder } from '../../../enums';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';

interface SubscriptionTabProps {
  tenantId: string | number;
}

const TIMELINE_LIMIT = 20;

/**
 * US-043: full implementation — active subscription card, historical
 * subscriptions table, and a subscription-scoped audit timeline. Replaces the
 * partial US-038 ST-006 version (active card only).
 */
const SubscriptionTab: React.FC<SubscriptionTabProps> = ({ tenantId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManageSubscription = usePermission('subscription:manage');

  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.desc, defaultOrderBy: 'end_date' });
  const [active, setActive] = useState<any>(null);
  const [historical, setHistorical] = useState<any[]>([]);
  const sortedHistorical = useMemo(() => sortData(historical, orderBy, order), [historical, orderBy, order]);
  const [loading, setLoading] = useState(true);
  const [savingAutoReminder, setSavingAutoReminder] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [cancellingPlanChange, setCancellingPlanChange] = useState(false);
  const [timeline, setTimeline] = useState<AuditLogEntry[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    TenantService.getSubscriptionHistory(tenantId)
      .then((response: any) => {
        setActive(response?.data?.active ?? null);
        setHistorical(response?.data?.historical ?? []);
      })
      .catch(() => {
        setActive(null);
        setHistorical([]);
      })
      .finally(() => setLoading(false));
  }, [tenantId]);

  const loadTimeline = useCallback(() => {
    setTimelineLoading(true);
    AuditService.list({ entityType: ['subscription'], tenantId: Number(tenantId), limit: TIMELINE_LIMIT })
      .then((response: any) => setTimeline(response?.data?.items ?? []))
      .catch(() => setTimeline([]))
      .finally(() => setTimelineLoading(false));
  }, [tenantId]);

  useEffect(() => {
    load();
    loadTimeline();
  }, [load, loadTimeline]);

  const handleToggleAutoReminder = useCallback(async () => {
    if (!active) return;
    setSavingAutoReminder(true);
    try {
      await SubscriptionService.update(active.id, { auto_reminder: !active.auto_reminder });
      setActive((prev: any) => (prev ? { ...prev, auto_reminder: !prev.auto_reminder } : prev));
    } finally {
      setSavingAutoReminder(false);
    }
  }, [active]);

  const handleCancelPlanChange = useCallback(async () => {
    if (!active) return;
    setCancellingPlanChange(true);
    try {
      await SubscriptionService.cancelPlanChange(active.id);
      toast.success(<Toast message={t('subscriptions.planChangeModal.pageTexts.cancelSuccess')} />, { icon: false });
      load();
    } finally {
      setCancellingPlanChange(false);
    }
  }, [active, load, t]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner />
      </div>
    );
  }

  if (!active && historical.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-muted">{t('tenants.detail.pageTexts.subscription.empty')}</p>
        {canManageSubscription && (
          <Button
            className="btn btn-primary"
            text={t('tenants.detail.pageTexts.subscription.createCta')}
            onClick={() => navigate(`${Modules.home}${AuthRoutes.subscriptions}${RouteParamsTypes.add}?tenantId=${tenantId}`)}
          />
        )}
      </div>
    );
  }

  const canRenew = active && ['expiring_soon', 'expired', 'pending'].includes(active.subscription_status_code ?? '');

  return (
    <div className="row g-4">
      {active && (
        <>
          <div className="col-md-3">
            <label className="form-label d-block text-muted mb-1">{t('subscriptions.form.pageTexts.fields.plan.label')}</label>
            <div>{active.plan_name ?? '—'}</div>
          </div>
          <div className="col-md-3">
            <label className="form-label d-block text-muted mb-1">{t('subscriptions.form.pageTexts.fields.billingCycle.label')}</label>
            <div>{active.billing_cycle_name ?? '—'}</div>
          </div>
          <div className="col-md-3">
            <label className="form-label d-block text-muted mb-1">{t('subscriptions.form.pageTexts.fields.startDate.label')}</label>
            <div>{String(active.start_date).slice(0, 10)}</div>
          </div>
          <div className="col-md-3">
            <label className="form-label d-block text-muted mb-1">{t('subscriptions.form.pageTexts.fields.endDate.label')}</label>
            <div>{String(active.end_date).slice(0, 10)}</div>
          </div>

          {active.pendingPlanChange && (
            <div className="col-12">
              <div className="alert alert-info d-flex justify-content-between align-items-center mb-0">
                <span>
                  {t('tenants.detail.pageTexts.subscription.planChangeScheduled', {
                    currentPlan: active.plan_name,
                    newPlan: active.pendingPlanChange.newPlanName,
                    effectiveDate: active.pendingPlanChange.effectiveDate,
                  })}
                </span>
                {canManageSubscription && (
                  <Button
                    className="btn btn-sm btn-outline-secondary"
                    text={t('subscriptions.planChangeModal.pageTexts.cancel')}
                    loading={cancellingPlanChange}
                    onClick={handleCancelPlanChange}
                  />
                )}
              </div>
            </div>
          )}

          <div className="col-md-3">
            <label className="form-label d-block text-muted mb-1">{t('subscriptions.list.pageTexts.table.columns.status')}</label>
            {active.subscription_status_name && active.subscription_status_colour ? (
              <StatusBadge label={active.subscription_status_name} colour={active.subscription_status_colour} />
            ) : (
              '—'
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label d-block text-muted mb-1">{t('subscriptions.list.pageTexts.table.columns.paymentStatus')}</label>
            {active.payment_status_name && active.payment_status_colour ? (
              <StatusBadge label={active.payment_status_name} colour={active.payment_status_colour} />
            ) : (
              '—'
            )}
          </div>
          <div className="col-md-3">
            <CustomizedSwitch
              label={t('subscriptions.form.pageTexts.fields.autoReminder.label')}
              name="auto_reminder"
              checked={!!active.auto_reminder}
              disabled={savingAutoReminder || !canManageSubscription}
              onChange={handleToggleAutoReminder}
            />
          </div>

          {canManageSubscription && (
            <div className="col-12 d-flex gap-2">
              {canRenew && (
                <Button
                  className="btn btn-outline-primary"
                  text={t('tenants.detail.pageTexts.subscription.renew')}
                  onClick={() => setShowRenewalModal(true)}
                />
              )}
              {!active.pendingPlanChange && (
                <Button
                  className="btn btn-outline-secondary"
                  text={t('tenants.detail.pageTexts.subscription.changePlan')}
                  onClick={() => setShowPlanChangeModal(true)}
                />
              )}
            </div>
          )}
        </>
      )}

      {!active && canManageSubscription && (
        <div className="col-12">
          <Button
            className="btn btn-primary"
            text={t('tenants.detail.pageTexts.subscription.createCta')}
            onClick={() => navigate(`${Modules.home}${AuthRoutes.subscriptions}${RouteParamsTypes.add}?tenantId=${tenantId}`)}
          />
        </div>
      )}

      {historical.length > 0 && (
        <div className="col-12">
          <h6>{t('tenants.detail.pageTexts.subscription.historyTitle')}</h6>
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  {[
                    { id: 'plan_name', label: t('subscriptions.form.pageTexts.fields.plan.label') },
                    { id: 'start_date', label: t('subscriptions.form.pageTexts.fields.startDate.label') },
                    { id: 'end_date', label: t('subscriptions.form.pageTexts.fields.endDate.label') },
                    { id: 'subscription_status_name', label: t('subscriptions.list.pageTexts.table.columns.status') },
                  ].map((col) => {
                    const isSorted = orderBy === col.id;
                    return (
                      <th
                        key={col.id}
                        className={`sorting ${isSorted ? (order === SortOrder.asc ? 'sorting_asc' : 'sorting_desc') : ''}`}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => {
                          if (orderBy === col.id) {
                            setOrder(order === SortOrder.asc ? SortOrder.desc : SortOrder.asc);
                          } else {
                            setOrderBy(col.id);
                            setOrder(SortOrder.asc);
                          }
                        }}
                      >
                        <span className="d-inline-flex align-items-center gap-1 w-100">
                          <span>{col.label}</span>
                          <span style={{ opacity: isSorted ? 1 : 0.4, transition: 'opacity 0.15s ease' }}>
                            {isSorted ? (
                              order === SortOrder.asc ? (
                                <i className="ri-arrow-up-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                              ) : (
                                <i className="ri-arrow-down-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                              )
                            ) : (
                              <i className="ri-arrow-up-down-line" style={{ fontSize: '13px' }} />
                            )}
                          </span>
                        </span>
                      </th>
                    );
                  })}
                  <th>{t('tenants.detail.pageTexts.subscription.viewPayments')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedHistorical.map((row: any) => (
                  <tr key={row.id}>
                    <td>{row.plan_name ?? '—'}</td>
                    <td>{String(row.start_date).slice(0, 10)}</td>
                    <td>{String(row.end_date).slice(0, 10)}</td>
                    <td>
                      {row.subscription_status_name && row.subscription_status_colour ? (
                        <StatusBadge label={row.subscription_status_name} colour={row.subscription_status_colour} />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <a
                        href={`${Modules.home}${AuthRoutes.payments}?subscriptionId=${row.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`${Modules.home}${AuthRoutes.payments}?subscriptionId=${row.id}`);
                        }}
                      >
                        {t('tenants.detail.pageTexts.subscription.viewPayments')} →
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="col-12">
        <h6>{t('tenants.detail.pageTexts.subscription.timelineTitle')}</h6>
        {timelineLoading ? (
          <Spinner />
        ) : (
          <ul className="list-unstyled">
            {timeline.map((entry) => (
              <li key={entry.id} className="mb-3 pb-3 border-bottom">
                <div className="d-flex justify-content-between">
                  <strong>{entry.action_description}</strong>
                  <span className="text-muted small">{new Date(entry.created_at).toLocaleString()}</span>
                </div>
                {entry.actor_full_name && (
                  <div className="text-muted small">
                    {t('tenants.detail.pageTexts.activity.by')} {entry.actor_full_name}
                  </div>
                )}
              </li>
            ))}
            {timeline.length === 0 && <div className="text-muted">{t('tenants.detail.pageTexts.activity.empty')}</div>}
          </ul>
        )}
        <a
          href={`${Modules.home}${AuthRoutes.auditLog}?tenantId=${tenantId}&entityType=subscription`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`${Modules.home}${AuthRoutes.auditLog}?tenantId=${tenantId}&entityType=subscription`);
          }}
          className="small"
        >
          {t('tenants.detail.pageTexts.activity.viewAllInAuditLog')}
        </a>
      </div>

      {active && (
        <>
          <RenewalModal
            open={showRenewalModal}
            subscriptionId={active.id}
            currentAmount={active.amount}
            currentCurrencyId={active.currency_id}
            currentCurrencyCode={active.currency_code ?? ''}
            onClose={() => setShowRenewalModal(false)}
            onRenewed={() => {
              setShowRenewalModal(false);
              load();
              loadTimeline();
            }}
          />

          <PlanChangeModal
            open={showPlanChangeModal}
            subscriptionId={active.id}
            productId={active.product_id}
            currentPlanId={active.plan_id}
            effectiveDate={String(active.end_date).slice(0, 10)}
            onClose={() => setShowPlanChangeModal(false)}
            onScheduled={() => {
              setShowPlanChangeModal(false);
              load();
              loadTimeline();
            }}
          />
        </>
      )}
    </div>
  );
};

export default SubscriptionTab;
